import React from 'react';
import clsx from 'clsx';
import savvyPortraitImg from '../../assets/savvy-avatar-portrait.png';
import savvyFullImg from '../../assets/savvy-avatar.png';

/**
 * SavvyHologramAvatar
 * 
 * High-fidelity holographic anime/cyber avatar for Savvy AI
 * Variants:
 *  - 'icon': Ultra-compact 24-32px circle for buttons / pills
 *  - 'badge': Compact 40-48px avatar for headers / message bubbles
 *  - 'portrait': 140-200px floating hologram for widgets and companion cards
 *  - 'pedestal': Full-height hologram with quantum emitter base for AI Studio
 */
export default function SavvyHologramAvatar({
  state = 'idle', // 'idle' | 'listening' | 'speaking' | 'paused'
  variant = 'portrait', // 'icon' | 'badge' | 'portrait' | 'pedestal'
  className,
  showSoundwaves = true,
  onClick
}) {
  const isSpeaking = state === 'speaking';
  const isListening = state === 'listening';
  const isPaused = state === 'paused';

  // 1. Icon variant (For floating launcher buttons and mini pills)
  if (variant === 'icon') {
    return (
      <div 
        onClick={onClick}
        className={clsx(
          "relative inline-flex items-center justify-center shrink-0 rounded-full overflow-hidden transition-all duration-300",
          isSpeaking && "ring-2 ring-cyan-400 shadow-[0_0_12px_rgba(56,189,248,0.7)] animate-pulse",
          isListening && "ring-2 ring-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.7)] animate-pulse",
          !isSpeaking && !isListening && "ring-1 ring-cyan-500/40",
          className || "w-7 h-7"
        )}
      >
        <div className={clsx(
          "absolute inset-0 rounded-full transition-opacity duration-300",
          isSpeaking ? "bg-cyan-500/30 blur-xs" : isListening ? "bg-emerald-500/30 blur-xs" : "bg-cyan-900/30"
        )} />
        <img 
          src={savvyPortraitImg} 
          alt="Savvy AI" 
          className="relative z-10 w-full h-full object-cover object-top scale-110"
        />
        {(isSpeaking || isListening) && (
          <span className={clsx(
            "absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full",
            isSpeaking ? "bg-cyan-400 animate-ping" : "bg-emerald-400 animate-ping"
          )} />
        )}
      </div>
    );
  }

  // 2. Badge variant (For headers & chat message headers)
  if (variant === 'badge') {
    return (
      <div 
        onClick={onClick}
        className={clsx(
          "relative flex items-center justify-center shrink-0 rounded-2xl p-1 bg-gradient-to-b from-slate-900 to-slate-950 border border-cyan-500/30 transition-all duration-300 shadow-sm",
          isSpeaking && "border-cyan-400 shadow-[0_0_15px_rgba(56,189,248,0.4)]",
          isListening && "border-emerald-400 shadow-[0_0_15px_rgba(52,211,153,0.4)]",
          className || "w-10 h-10"
        )}
      >
        <div className={clsx(
          "absolute inset-1 rounded-xl transition-all duration-300",
          isSpeaking ? "bg-cyan-500/25 blur-sm animate-pulse" : isListening ? "bg-emerald-500/25 blur-sm animate-pulse" : "bg-cyan-950/30"
        )} />
        <img 
          src={savvyPortraitImg} 
          alt="Savvy AI Avatar" 
          className="relative z-10 w-full h-full object-contain object-top"
        />
        {showSoundwaves && (isSpeaking || isListening) && (
          <span className="absolute -bottom-1 -right-1 flex h-3 w-3">
            <span className={clsx(
              "absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping",
              isSpeaking ? "bg-cyan-400" : "bg-emerald-400"
            )} />
            <span className={clsx(
              "relative inline-flex rounded-full h-3 w-3",
              isSpeaking ? "bg-cyan-500" : "bg-emerald-500"
            )} />
          </span>
        )}
      </div>
    );
  }

  // 3. Portrait variant (For widget companion card & drawer)
  if (variant === 'portrait') {
    return (
      <div className={clsx("relative flex flex-col items-center justify-center select-none", className)}>
        {/* Ambient Backlight Glow */}
        <div className={clsx(
          "absolute rounded-full transition-all duration-500 pointer-events-none",
          isSpeaking 
            ? "w-40 h-40 bg-cyan-400/35 blur-2xl animate-pulse" 
            : isListening 
              ? "w-36 h-36 bg-emerald-400/30 blur-2xl animate-pulse" 
              : "w-32 h-32 bg-cyan-600/20 blur-xl"
        )} />

        {/* Floating Hologram Figure */}
        <div className="relative z-10 flex flex-col items-center">
          <img 
            src={savvyPortraitImg} 
            alt="Savvy Hologram Avatar" 
            className={clsx(
              "object-contain filter transition-all duration-500 pointer-events-none",
              isSpeaking 
                ? "h-44 sm:h-52 drop-shadow-[0_0_24px_rgba(56,189,248,0.7)] animate-[bounce_4s_infinite]" 
                : isListening 
                  ? "h-40 sm:h-48 drop-shadow-[0_0_20px_rgba(52,211,153,0.6)]" 
                  : "h-36 sm:h-44 drop-shadow-[0_0_14px_rgba(56,189,248,0.4)]"
            )}
            style={{
              animation: isSpeaking 
                ? 'floatHolo 2.5s ease-in-out infinite' 
                : 'floatHolo 4s ease-in-out infinite'
            }}
          />

          {/* Active Soundwaves Equalizer */}
          {showSoundwaves && (isSpeaking || isListening) && (
            <div className="absolute -bottom-2 z-20 flex items-center gap-1 px-3 py-1 rounded-full bg-slate-950/80 border border-cyan-400/40 backdrop-blur-md shadow-lg">
              <span className={clsx("w-1 rounded-full animate-[pulse_0.7s_infinite]", isSpeaking ? "bg-cyan-400 h-3" : "bg-emerald-400 h-2")} />
              <span className={clsx("w-1 rounded-full animate-[pulse_0.9s_infinite_0.2s]", isSpeaking ? "bg-cyan-300 h-5" : "bg-emerald-300 h-3")} />
              <span className={clsx("w-1.5 rounded-full animate-[pulse_0.6s_infinite_0.1s]", isSpeaking ? "bg-indigo-400 h-6" : "bg-emerald-400 h-4")} />
              <span className={clsx("w-1 rounded-full animate-[pulse_0.8s_infinite_0.3s]", isSpeaking ? "bg-cyan-300 h-4" : "bg-emerald-300 h-2.5")} />
              <span className={clsx("w-1 rounded-full animate-[pulse_0.7s_infinite_0.15s]", isSpeaking ? "bg-cyan-400 h-2.5" : "bg-emerald-400 h-2")} />
            </div>
          )}
        </div>

        {/* Hologram Base Projector Disc */}
        <div className="relative z-10 w-28 sm:w-32 flex flex-col items-center -mt-2">
          <div className="w-20 h-3 bg-gradient-to-t from-cyan-400/30 to-transparent blur-xs" />
          <div className="w-24 h-1.5 rounded-full bg-cyan-400/50 blur-[1px] animate-pulse" />
          <div className="w-28 h-1.5 rounded-full bg-slate-800 border border-cyan-400/40 -mt-0.5 shadow-[0_0_10px_rgba(56,189,248,0.4)]" />
        </div>
      </div>
    );
  }

  // 4. Pedestal variant (Full-height projector for AI Intelligence Studio)
  return (
    <div className={clsx("relative flex flex-col items-center justify-center select-none py-4", className)}>
      {/* Background Beam & Grid effect */}
      <div className={clsx(
        "absolute rounded-full transition-all duration-700 pointer-events-none",
        isSpeaking 
          ? "w-64 h-64 bg-cyan-400/30 blur-3xl animate-pulse" 
          : isListening 
            ? "w-56 h-56 bg-emerald-400/25 blur-3xl animate-pulse" 
            : "w-48 h-48 bg-cyan-600/15 blur-2xl"
      )} />

      {/* Hologram Figure */}
      <div className="relative z-10 flex flex-col items-center">
        <img 
          src={savvyFullImg} 
          alt="Savvy Hologram Full Pedestal" 
          className={clsx(
            "object-contain filter transition-all duration-500 pointer-events-none",
            isSpeaking 
              ? "max-h-72 sm:max-h-84 drop-shadow-[0_0_32px_rgba(56,189,248,0.7)]" 
              : isListening 
                ? "max-h-68 sm:max-h-80 drop-shadow-[0_0_24px_rgba(52,211,153,0.6)]" 
                : "max-h-64 sm:max-h-76 drop-shadow-[0_0_18px_rgba(56,189,248,0.4)]"
          )}
          style={{
            animation: isSpeaking 
              ? 'floatHolo 2.8s ease-in-out infinite' 
              : 'floatHolo 4.2s ease-in-out infinite'
          }}
        />

        {/* Live Audio Spectrum Equalizer */}
        {showSoundwaves && (isSpeaking || isListening) && (
          <div className="absolute bottom-12 z-20 flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-slate-950/85 border border-cyan-400/40 backdrop-blur-md shadow-xl">
            <span className={clsx("w-1 rounded-full animate-[pulse_0.7s_infinite]", isSpeaking ? "bg-cyan-400 h-4" : "bg-emerald-400 h-2.5")} />
            <span className={clsx("w-1.5 rounded-full animate-[pulse_0.9s_infinite_0.2s]", isSpeaking ? "bg-cyan-300 h-7" : "bg-emerald-300 h-4")} />
            <span className={clsx("w-1.5 rounded-full animate-[pulse_0.6s_infinite_0.1s]", isSpeaking ? "bg-indigo-400 h-8" : "bg-emerald-400 h-5")} />
            <span className={clsx("w-1 rounded-full animate-[pulse_0.8s_infinite_0.3s]", isSpeaking ? "bg-cyan-300 h-6" : "bg-emerald-300 h-3.5")} />
            <span className={clsx("w-1 rounded-full animate-[pulse_0.7s_infinite_0.15s]", isSpeaking ? "bg-cyan-400 h-3.5" : "bg-emerald-400 h-2.5")} />
          </div>
        )}
      </div>

      {/* Quantum Base Emitter Disc */}
      <div className="relative z-10 w-48 sm:w-56 flex flex-col items-center -mt-6">
        <div className="w-36 h-8 bg-gradient-to-t from-cyan-400/40 to-transparent blur-sm" />
        <div className="w-44 h-2.5 rounded-full bg-cyan-400/60 blur-[2px] animate-pulse" />
        <div className="w-48 h-3 rounded-full bg-slate-800 border border-cyan-400/50 -mt-1 shadow-[0_0_18px_rgba(56,189,248,0.5)]" />
        <span className="text-[10px] tracking-widest text-cyan-400/80 font-mono uppercase mt-2">
          Quantum Neural Hologram Emitter
        </span>
      </div>
    </div>
  );
}
